import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Box, Layers, X, ZoomIn, ChevronLeft as ChevronLeftIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Navbar from './Navbar';
import Footer from './Footer';

// Lightbox Component
const Lightbox = ({ image, onClose }) => {
  if (!image) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95"
        onClick={onClose}
      >
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors"
          onClick={onClose}
        >
          <X size={24} className="text-white" />
        </motion.button>
        
        <motion.img
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          src={image}
          alt="Enlarged view"
          className="max-w-[90vw] max-h-[90vh] object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </motion.div>
    </AnimatePresence>
  );
};

// Image Carousel Component
const ImageCarousel = ({ images, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) {
    return (
      <div className="card-technical p-12 text-center h-80 flex items-center justify-center">
        <p className="text-[#94A3B8] text-lg">Images coming soon...</p>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="relative">
      <div className="relative overflow-hidden border border-[#6B8CAE]/30 bg-white/5 h-80">
        <motion.img
          key={currentIndex}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.3 }}
          src={images[currentIndex]}
          alt={`Image ${currentIndex + 1}`}
          className="w-full h-full object-contain cursor-zoom-in"
          onClick={() => onImageClick(images[currentIndex])}
        />
        
        <div 
          className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/20 cursor-zoom-in"
          onClick={() => onImageClick(images[currentIndex])}
        >
          <div className="w-12 h-12 bg-[#6B8CAE] flex items-center justify-center">
            <ZoomIn size={24} className="text-white" />
          </div>
        </div>

        {images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#0F172A]/80 border border-[#6B8CAE]/50 flex items-center justify-center hover:bg-[#6B8CAE]/20 transition-colors"
            >
              <ChevronLeftIcon size={20} className="text-white" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#0F172A]/80 border border-[#6B8CAE]/50 flex items-center justify-center hover:bg-[#6B8CAE]/20 transition-colors"
            >
              <ChevronRight size={20} className="text-white" />
            </button>
          </>
        )}

        <div className="absolute bottom-3 right-3 bg-[#0F172A]/80 px-3 py-1 text-white text-sm font-mono">
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      {images.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
          {images.map((img, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`flex-shrink-0 w-20 h-16 border-2 overflow-hidden transition-all ${
                index === currentIndex 
                  ? 'border-[#6B8CAE] opacity-100' 
                  : 'border-transparent opacity-50 hover:opacity-75'
              }`}
            >
              <img
                src={img}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ModelingPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [lightboxImage, setLightboxImage] = useState(null);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const texts = {
    en: {
      back: 'Back to Services',
      title: '3D Modeling',
      description: '3D modeling forms the foundation of modern product development and manufacturing engineering. Accurate digital models allow ideas to be evaluated, tested and refined before committing to physical production. We create 3D models that support the full development cycle, from early concept exploration to final manufacturing documentation.',
      clickToZoom: 'Click image to enlarge',
      servicesTitle: 'Our Services',
      items: [
        'Part and assembly modeling for mechanical systems',
        'Surface modeling for complex shapes and enclosures',
        'Sheet metal design and unfolding',
        'Weldment and structural frame design',
        'Design for manufacturing (DFM) optimization',
        'Legacy data conversion and 3D reconstruction'
      ],
      softwareTitle: 'Software Expertise',
      software: [
        'CATIA V5 (Part Design, Assembly, GSD)',
        'SolidWorks',
        'Autodesk Inventor',
        'Siemens NX',
        'Creo Parametric'
      ]
    },
    bg: {
      back: 'Обратно към Услуги',
      title: '3D Моделиране',
      description: '3D моделирането е основата на съвременното разработване на продукти и производствено инженерство. Точните цифрови модели позволяват идеите да бъдат оценени, тествани и усъвършенствани преди да се пристъпи към физическо производство. Ние създаваме 3D модели, които поддържат пълния цикъл на разработка, от ранно проучване на концепцията до окончателна производствена документация.',
      clickToZoom: 'Кликнете върху изображението за увеличаване',
      servicesTitle: 'Нашите услуги',
      items: [
        'Моделиране на части и сглобки за механични системи',
        'Повърхностно моделиране за сложни форми и корпуси',
        'Проектиране на ламарина и разгъване',
        'Проектиране на заварени конструкции и рамки',
        'Оптимизация за производство (DFM)',
        'Конвертиране на стари данни и 3D реконструкция'
      ],
      softwareTitle: 'Софтуерна експертиза',
      software: [
        'CATIA V5 (Part Design, Assembly, GSD)',
        'SolidWorks',
        'Autodesk Inventor',
        'Siemens NX',
        'Creo Parametric'
      ]
    }
  };

  const t = texts[language] || texts.en;

  // 3D Modeling images
  const images = [
    'https://images.unsplash.com/photo-1769147339214-076740872485?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHwzRCUyMENBRCUyMG1vZGVsaW5nJTIwbWVjaGFuaWNhbCUyMGVuZ2luZWVyaW5nJTIwZGVzaWdufGVufDB8fHx8MTc3NTkwNzMzMnww&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1637251617875-e00c25a5f58e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHwzRCUyMENBRCUyMG1vZGVsaW5nJTIwbWVjaGFuaWNhbCUyMGVuZ2luZWVyaW5nJTIwZGVzaWdufGVufDB8fHx8MTc3NTkwNzMzMnww&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1637946902191-57cf1fe3c056?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHwzRCUyMENBRCUyMG1vZGVsaW5nJTIwbWVjaGFuaWNhbCUyMGVuZ2luZWVyaW5nJTIwZGVzaWdufGVufDB8fHx8MTc3NTkwNzMzMnww&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1702845607265-8b4d523d9415?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHwzRCUyMENBRCUyMG1vZGVsaW5nJTIwbWVjaGFuaWNhbCUyMGVuZ2luZWVyaW5nJTIwZGVzaWdufGVufDB8fHx8MTc3NTkwNzMzMnww&ixlib=rb-4.1.0&q=85'
  ];

  return (
    <div className="min-h-screen bg-[#0F172A]">
      <Navbar />
      
      {lightboxImage && (
        <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-[#6B8CAE] hover:text-white transition-colors mb-8"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <span className="font-mono text-sm uppercase tracking-wider">{t.back}</span>
          </motion.button>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-[#6B8CAE]/10 border border-[#6B8CAE]/30 flex items-center justify-center">
                <Box size={32} className="text-[#6B8CAE]" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-white" data-testid="modeling-page-title">
                {t.title}
              </h1>
            </div>
            <p className="text-[#94A3B8] text-lg leading-relaxed max-w-4xl">
              {t.description}
            </p>
          </motion.div>

          {/* Content Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid lg:grid-cols-2 gap-12 mb-12"
          >
            {/* Services List */}
            <div className="card-technical p-8">
              <h2 className="text-2xl font-semibold text-white mb-8 flex items-center space-x-3">
                <Layers size={24} className="text-[#6B8CAE]" />
                <span>{t.servicesTitle}</span>
              </h2>
              <ul className="space-y-4">
                {t.items.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start space-x-3 text-[#94A3B8]"
                  >
                    <ChevronRight size={20} className="text-[#6B8CAE] flex-shrink-0 mt-0.5" />
                    <span className="text-lg">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Images Carousel */}
            <div>
              <ImageCarousel 
                images={images} 
                onImageClick={(img) => setLightboxImage(img)}
              />
              <p className="text-[#6B8CAE] text-sm mt-3 text-center font-mono">
                <ZoomIn size={14} className="inline mr-1" />
                {t.clickToZoom}
              </p>
            </div>
          </motion.div>

          {/* Software Expertise */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card-technical p-8"
          >
            <h2 className="text-2xl font-semibold text-white mb-6 flex items-center space-x-3">
              <Box size={24} className="text-[#6B8CAE]" />
              <span>{t.softwareTitle}</span>
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {t.software.map((sw, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-[#6B8CAE]/10 border border-[#6B8CAE]/30 px-4 py-3 text-[#94A3B8] hover:bg-[#6B8CAE]/20 hover:text-white transition-colors"
                >
                  {sw}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ModelingPage;
